import React from "react";
import { NetworkStatus } from "@apollo/client";
import { RESOURCE_NAME } from "common/authorization";
import {
  FirmImportErrorType,
  FirmSource,
  useAccountFirmImportsQuery,
  useExportGeoJsonFirmsMutation,
  useGetAccountFirmsQuery,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { spacing } from "../../../../stitches.config";
import { AuthContext } from "../../../Authorization/AuthContext";
import { Box, FlexRow } from "../../../Common/__styles__/Layout";
import { Button } from "../../../Common/Buttons/Button";
import Divider from "../../../Common/Divider";
import { EmptyState } from "../../../Common/EmptyState";
import { Icons } from "../../../Common/Icons";
import { useManualPaginationConfig } from "../../../Common/Tables/hooks";
import {
  Header,
  HeaderTitle,
  Section,
  SectionTitle,
} from "../../__styles__/Content";
import { RecentJob } from "../RecentJobsTable";
import FirmImports from "./FirmImports";
import FirmLayer from "./FirmLayer";
import { useFirmCreateImportModal } from "./forms/FirmCreateImport";

export const Firms = ({ pollInterval = 10000 }: { pollInterval?: number }) => {
  const { account, authorized } = React.useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const {
    data: firmData,
    loading: loadingFirmData,
    error: firmError,
  } = useGetAccountFirmsQuery({ variables: { includeDisabledFirms: true } });

  const firms = firmData?.account?.firms;
  const hasFirms = !loadingFirmData && firms && firms.length;

  if (firmError) {
    throw new Error(`Error loading firms: ${firmError.message}`);
  }

  const manualPaginationConfig = useManualPaginationConfig({ pageSize: 10 });
  let variables = {
    page: manualPaginationConfig.pagination.pageIndex + 1,
    pageSize: manualPaginationConfig.pagination.pageSize,
  };

  const {
    data: currentFirmImportsData,
    networkStatus,
    loading: loadingFirmImports,
    error: firmImportsError,
    previousData: previousFirmImportsData,
    refetch: refetchFirmImports,
  } = useAccountFirmImportsQuery({
    fetchPolicy: "no-cache",
    pollInterval,
    variables: {
      id: account?.id,
      ...variables,
    },
  });

  const [exportGeoJsonFirms] = useExportGeoJsonFirmsMutation({
    onCompleted: () => {
      addSuccessToast(
        `The FIRMs are being exported. You will receive an email with the link`,
      );
    },
    onError: () => {
      addErrorToast(
        `There was an error exporting the FIRMs. Please try again. If the problem persists, please email support@withforerunner.com`,
      );
    },
  });

  const firmImportsResponse =
    networkStatus === NetworkStatus.setVariables ?
      previousFirmImportsData
    : currentFirmImportsData;

  const canCreateFIRM = authorized({
    resource: RESOURCE_NAME.FIRM,
    permission: "create",
  });

  const [openCreateFIRMModal] = useFirmCreateImportModal({
    onScheduledFirmImport: refetchFirmImports,
  });

  return (
    <Box>
      <Header>
        <HeaderTitle>FIRM settings</HeaderTitle>
        <FlexRow style={{ gap: spacing.m.value }}>
          <Button
            onPress={async () => await exportGeoJsonFirms()}
            size="small"
            variant="outline"
            aria-label="Export FIRMs"
            iconStart="download"
            title="Export"
          />
          <Button
            variant="outline"
            isDisabled={!canCreateFIRM}
            onPress={openCreateFIRMModal}
            size="small"
            iconStart="plus"
            title="Create FIRM"
          />
        </FlexRow>
      </Header>
      <Divider />
      <Section>
        <SectionTitle>FIRM Layers</SectionTitle>
      </Section>
      {!loadingFirmData &&
        (hasFirms ?
          firms.map(firm => (
            <FirmLayer
              firmLayer={firm}
              key={firm.name}
              onScheduledFirmImport={refetchFirmImports}
            />
          ))
        : <EmptyState
            icon="add-raster"
            title="No FIRM data for this account"
            body="Create FIRM to get started."
            paddingTop={spacing["2xl"].value}
            paddingBottom={spacing["2xl"].value}
          />)}
      <Section>
        <SectionTitle>Recent imports</SectionTitle>
        <FirmImports
          recentJobs={
            firmImportsResponse?.account?.firmImports.data.map(firmImport => {
              return {
                id: firmImport.id,
                name: firmImport.firm.name,
                error: firmImport.error?.message,
                createdAt: firmImport.createdAt,
                stoppedAt: firmImport.stoppedAt ?? undefined,
                status: firmImport.status,
                csvDownloadHref:
                  (
                    firmImport.error?.type ===
                    FirmImportErrorType.GEOJSON_VALIDATION_FAILURE
                  ) ?
                    `/api/firm-import/${
                      firmImport!.id
                    }/validation-errors?accountId=${account?.id}`
                  : undefined,
                icon:
                  firmImport.firm.source === FirmSource.FEMA ?
                    Icons.ARC_GIS
                  : Icons.GPKG,
              } satisfies RecentJob;
            }) ?? []
          }
          error={firmImportsError}
          loading={loadingFirmImports}
          manualPaginationConfig={{
            ...manualPaginationConfig,
            pageCount:
              firmImportsResponse?.account?.firmImports.pageInfo.totalPages ??
              1,
          }}
        />
      </Section>
    </Box>
  );
};

export default Firms;
